import * as React from 'react';
import Paper from './components/Paper';
import Flipbook from './components/Flipbook';
import Book from './components/flipbook/Book';
import Presentation from './components/Presentation';
import PaperMozilla from './components/PaperMozilla';
import PaperReactPdf from './components/PaperReactPdf';
import { readTrackingConfig } from './tracking';
import { readFormConfig } from './components/pdfForm';


const App = () => {
    const rootDataset = document.getElementById('root').dataset;
    const params = new URLSearchParams(window.location.search);

    // Production: Django injects data-document etc. on the root div.
    // Dev: fall back to URL params (?doc=<url>&type=pdf&dl=false) so the CRA dev
    // server can render a real PDF without the Django backend. Further dev fallback:
    // a public test PDF so ?viewer=reactpdf works without any params.
    const DEV_DEFAULT_PDF =
        'https://mozilla.github.io/pdf.js/web/compressed.tracemonkey-pldi-09.pdf';
    const documentUrl =
        rootDataset.document ||
        params.get('doc') ||
        (process.env.NODE_ENV === 'development' ? DEV_DEFAULT_PDF : undefined);
    const documentType = rootDataset.type || params.get('type') || 'pdf';
    const dlEnabled = rootDataset.dl || params.get('dl') || 'false';
    // printEnabled is independent; if Django doesn't inject it yet, fall back to dlEnabled
    // so existing docs still get print when download is on.
    const printEnabled =
        rootDataset.print || params.get('print') || dlEnabled;
    const showBranding = rootDataset.branding || params.get('branding') || 'false';
    const brandingLink = rootDataset.brandinglink || params.get('brandinglink') || '';
    const brandName = rootDataset.brandname || params.get('brandname') || 'PDF Deck';
    // The sharer's brand kit (paid workspaces): their logo and accent colour.
    const brandLogo = rootDataset.brandlogo || '';
    const brandColor = rootDataset.brandcolor || '';
    const title = rootDataset.title || params.get('title') || '';
    // Owner's call-to-action button and the visitor "Report" link.
    const cta = rootDataset.ctaurl ? { label: rootDataset.ctalabel, url: rootDataset.ctaurl } : null;
    const reportUrl = rootDataset.reporturl || '';
    // Per-page engagement reporting (see tracking.js). Empty when not enabled.
    const tracking = readTrackingConfig(rootDataset, params);
    // Fillable PDFs: where responses go (see components/pdfForm.js).
    const formConfig = readFormConfig(rootDataset, params);

    // Viewer variant switcher. Default for PDF mode is the new react-pdf viewer.
    // ?viewer=current falls back to the old @react-pdf-viewer build, ?viewer=mozilla iframes
    // Mozilla's reference viewer (benchmark only).
    const viewerVariant = params.get('viewer');

    // Flipbooks use the page-turn Book unless the document is on the classic
    // design (data-flipbookstyle: older flipbooks keep it so nothing changes
    // under their owners). ?viewer=classic / ?viewer=book override for testing.
    const flipbookStyle = viewerVariant === 'classic' || viewerVariant === 'book'
        ? viewerVariant
        : (rootDataset.flipbookstyle || 'book');
    if (documentType === 'book' || (documentType === 'flipbook' && flipbookStyle !== 'classic')) {
        // auto | single | double — the document's page layout setting.
        const layout = rootDataset.layout || params.get('layout') || 'auto';
        return <Book documentUrl={documentUrl} dlEnabled={dlEnabled} showBranding={showBranding} brandingLink={brandingLink} brandName={brandName} brandLogo={brandLogo} brandColor={brandColor} title={title} cta={cta} reportUrl={reportUrl} tracking={tracking} layout={layout} />;
    }
    if (documentType === 'flipbook') {
        return <Flipbook documentUrl={documentUrl} dlEnabled={dlEnabled} showBranding={showBranding} brandingLink={brandingLink} brandName={brandName} tracking={tracking} />;
    }
    if (documentType === 'presentation') {
        return <Presentation documentUrl={documentUrl} dlEnabled={dlEnabled} showBranding={showBranding} brandingLink={brandingLink} brandName={brandName} brandColor={brandColor} cta={cta} tracking={tracking} />;
    }
    if (viewerVariant === 'mozilla') {
        return <PaperMozilla documentUrl={documentUrl} dlEnabled={dlEnabled} showBranding={showBranding} brandingLink={brandingLink} />;
    }
    if (viewerVariant === 'current') {
        return <Paper documentUrl={documentUrl} dlEnabled={dlEnabled} showBranding={showBranding} />;
    }
    return (
        <PaperReactPdf
            documentUrl={documentUrl}
            dlEnabled={dlEnabled}
            printEnabled={printEnabled}
            showBranding={showBranding}
            brandingLink={brandingLink}
            brandLogo={brandLogo}
            brandColor={brandColor}
            cta={cta}
            reportUrl={reportUrl}
            tracking={tracking}
            formConfig={formConfig}
            title={title}
        />
    );
};

export default App;
